import React, { useState, useEffect, useCallback } from 'react'
import { 
  Plus, 
  Edit, 
  Trash2, 
  RefreshCw,
  Search,
  Filter,
  X,
  ChevronDown,
  Eye,
  CheckCircle,
  XCircle
} from 'lucide-react'
import { leaveTypeService } from '../../../services/leaveService'
// import { usePermissions } from '../../../hooks/usePermissions'
// import { PERMISSIONS } from '../../../config/roles'
// import AuthService from '../../../services/authService'

// Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center space-x-2 ${
    type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
    <span>{message || 'Action completed'}</span>
    <button onClick={onClose} className="ml-2">
      <X className="h-4 w-4" />
    </button>
  </div>
)

// Toggle Switch Component
const ToggleSwitch = ({ checked, onChange, disabled = false, label }) => (
  <div className="flex items-center">
    <button
      type="button"
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
        checked ? 'bg-orange-600' : 'bg-gray-200'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      role="switch"
      aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
    >
      <span
        aria-hidden="true"
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
    {label && (
      <span className="ml-3 text-sm font-medium text-gray-900">
        {label}
      </span>
    )}
  </div>
)

const LeaveTypes = () => {
  const [leaveTypes, setLeaveTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedLeaveType, setSelectedLeaveType] = useState(null)
  const [toastMessage, setToastMessage] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    isActive: true
  })

  // const { hasPermission } = usePermissions()
  // const canManage = hasPermission(PERMISSIONS.HR.MANAGE_LEAVE_TYPES) // Currently not using permissions

  // Toast notification function
  const showToast = (message, type = 'success') => {
    setToastMessage({ message, type })
    setTimeout(() => setToastMessage(null), 3000)
  }

  const resetFormData = () => {
    setFormData({
      name: '',
      isActive: true
    })
  }

  const fetchLeaveTypes = useCallback(async () => {
    try {
      setLoading(true)
      const data = await leaveTypeService.getAllLeaveTypes()
      console.log('Leave Types API Response:', data)
      
      // Handle the API response structure
      let leaveTypesArray = []
      if (data?.data?.leaveTypes && Array.isArray(data.data.leaveTypes)) {
        leaveTypesArray = data.data.leaveTypes
      } else if (data?.leaveTypes && Array.isArray(data.leaveTypes)) {
        leaveTypesArray = data.leaveTypes
      } else if (Array.isArray(data?.data)) {
        leaveTypesArray = data.data
      } else if (Array.isArray(data)) {
        leaveTypesArray = data
      } else {
        console.warn('Unexpected API response structure:', data)
        leaveTypesArray = []
      }
      
      console.log('Parsed Leave Types Array:', leaveTypesArray)
      if (leaveTypesArray.length > 0) {
        console.log('Sample Leave Type Object:', leaveTypesArray[0])
      }
      
      setLeaveTypes(leaveTypesArray)
    } catch (error) {
      console.error('Error fetching leave types:', error)
      // Mock data for development
      setLeaveTypes([
        {
          _id: 'mock-1',
          id: 1,
          name: 'Annual Leave',
          isActive: true,
          createdAt: new Date().toISOString(),
          createdBy: 'System'
        },
        {
          _id: 'mock-2',
          id: 2,
          name: 'Sick Leave',
          isActive: true,
          createdAt: new Date().toISOString(),
          createdBy: 'System'
        }
      ])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchLeaveTypes()
  }, [fetchLeaveTypes])

  const handleCreateLeaveType = async () => {
    try {
      const createData = {
        ...formData
      }
      
      await leaveTypeService.createLeaveType(createData)
      
      // Show success message
      if (window.showToast) {
        window.showToast('Leave type created successfully!', 'success')
      } else {
        // Fallback toast implementation
        showToast('Leave type created successfully!', 'success')
      }
      
      resetFormData()
      setShowCreateModal(false)
      fetchLeaveTypes()
    } catch (error) {
      console.error('Error creating leave type:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create leave type'
      
      if (window.showToast) {
        window.showToast(errorMessage, 'error')
      } else {
        showToast(errorMessage, 'error')
      }
    }
  }

  const handleViewClick = (leaveType) => {
    setSelectedLeaveType(leaveType)
    setShowViewModal(true)
  }

  const handleEditClick = (leaveType) => {
    setSelectedLeaveType(leaveType)
    setFormData({
      name: leaveType.name,
      isActive: leaveType.isActive
    })
    setShowEditModal(true)
  }

  const handleEditLeaveType = async () => {
    try {
      const updateData = {
        ...formData
      }
      
      await leaveTypeService.updateLeaveType(selectedLeaveType._id || selectedLeaveType.id, updateData)
      
      // Show success message
      if (window.showToast) {
        window.showToast('Leave type updated successfully!', 'success')
      } else {
        showToast('Leave type updated successfully!', 'success')
      }
      
      resetFormData()
      setSelectedLeaveType(null)
      setShowEditModal(false)
      fetchLeaveTypes()
    } catch (error) {
      console.error('Error updating leave type:', error)
      
      let errorMessage = 'Failed to update leave type'
      
      if (error.response?.status === 404) {
        errorMessage = 'Leave type not found or update endpoint not implemented'
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }
      
      if (window.showToast) {
        window.showToast(errorMessage, 'error')
      } else {
        showToast(errorMessage, 'error')
      }
    }
  }

  const handleDeleteClick = (leaveType) => {
    setSelectedLeaveType(leaveType)
    setShowDeleteModal(true)
  }

  const handleDeleteLeaveType = async () => {
    try {
      await leaveTypeService.deleteLeaveType(selectedLeaveType._id || selectedLeaveType.id)
      
      // Show success message
      if (window.showToast) {
        window.showToast('Leave type deleted successfully!', 'success')
      } else {
        showToast('Leave type deleted successfully!', 'success')
      }
      
      setSelectedLeaveType(null)
      setShowDeleteModal(false)
      fetchLeaveTypes()
    } catch (error) {
      console.error('Error deleting leave type:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete leave type'
      
      if (window.showToast) {
        window.showToast(errorMessage, 'error')
      } else {
        showToast(errorMessage, 'error')
      }
    }
  }

  const filteredLeaveTypes = leaveTypes.filter(leaveType => {
    const matchesSearch = leaveType.name?.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && leaveType.isActive) ||
                         (statusFilter === 'inactive' && !leaveType.isActive)
    
    return matchesSearch && matchesStatus
  })

  const handleClearFilters = () => {
    setSearchTerm('')
    setStatusFilter('all')
  }

  const hasActiveFilters = searchTerm || statusFilter !== 'all'

  // Toggle Switch Component
  const ToggleSwitch = ({ checked, onChange, label }) => {
    return (
      <div className="flex items-center space-x-3">
        <button
          type="button"
          onClick={() => onChange(!checked)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
            checked ? 'bg-orange-600' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${
              checked ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
        <span className={`text-sm font-medium ${checked ? 'text-orange-600' : 'text-gray-600'}`}>
          {label}
        </span>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-orange-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toastMessage && (
        <Toast
          message={toastMessage.message}
          type={toastMessage.type}
          onClose={() => setToastMessage(null)}
        />
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leave Types</h1>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={fetchLeaveTypes}
            className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
          <button
            onClick={() => {
              resetFormData()
              setShowCreateModal(true)
            }}
            className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus className="h-5 w-5" />
            Add Leave Type
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Filter Leave Types</h2>
          </div>
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 text-orange-600 border border-orange-600 rounded-lg hover:bg-orange-50 transition-colors"
            >
              Clear Filters
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-3 w-full border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          
          {/* Status Filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none pr-10 bg-white"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Results Summary */}
        <div className="text-sm text-gray-600 mb-2">
          Showing {filteredLeaveTypes.length} of {leaveTypes.length} leave types
        </div>
      </div>

      {/* Leave Types Table */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        {filteredLeaveTypes.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-500 mb-2">No leave types found</div>
            <p className="text-sm text-gray-400">
              {hasActiveFilters ? 'Try adjusting your filters' : 'Get started by creating your first leave type'}
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Leave Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredLeaveTypes.map((leaveType) => (
                <tr key={leaveType._id || leaveType.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {leaveType.createdAt ? new Date(leaveType.createdAt).toLocaleDateString() : new Date().toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {leaveType.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {typeof leaveType.createdBy === 'object' && leaveType.createdBy?.username 
                        ? leaveType.createdBy.username 
                        : leaveType.createdBy || 'System'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      leaveType.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {leaveType.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                    <div className="flex items-center justify-center space-x-1">
                      <button
                        onClick={() => handleViewClick(leaveType)}
                        className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditClick(leaveType)}
                        className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(leaveType)}
                        className="text-gray-400 hover:text-red-600 p-1 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Create New Leave Type
              </h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Leave Type Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="e.g., Annual Leave"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <ToggleSwitch
                    checked={formData.isActive}
                    onChange={(checked) => setFormData(prev => ({ ...prev, isActive: checked }))}
                    // label="Active"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateLeaveType}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedLeaveType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Edit Leave Type
              </h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Leave Type Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="e.g., Annual Leave"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <ToggleSwitch
                    checked={formData.isActive}
                    onChange={(checked) => setFormData(prev => ({ ...prev, isActive: checked }))}
                    // label="Active"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEditLeaveType}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedLeaveType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Leave Type Details
              </h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Leave Type Name
                </label>
                <div className="text-sm text-gray-900 p-2 bg-gray-50 rounded">
                  {selectedLeaveType.name}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <div className="p-2">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    selectedLeaveType.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {selectedLeaveType.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Created Date
                </label>
                <div className="text-sm text-gray-900 p-2 bg-gray-50 rounded">
                  {selectedLeaveType.createdAt ? new Date(selectedLeaveType.createdAt).toLocaleDateString() : 'N/A'}
                </div>
              </div>
            </div>
            <div className="flex justify-end pt-4">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Delete Leave Type
              </h3>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{selectedLeaveType?.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteLeaveType}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default LeaveTypes